import { useState } from 'react';
import {
  FaRegFrown,
  FaRegMeh,
  FaRegSmile,
  FaSmile,
  FaGrinStars,
  FaCheckCircle,
  FaUser,
  FaBuilding,
  FaMapMarkerAlt,
  FaChalkboardTeacher,
  FaCalendarAlt,
  FaBook
} from 'react-icons/fa';
import './FeedbackForm.css';

const LOCAL_API_BASE_URL = "http://localhost:5000";
const RENDER_API_BASE_URL = "https://wecareehs-backend.onrender.com";

const API_BASE_URL = process.env.REACT_APP_USE_RENDER === "true" ? RENDER_API_BASE_URL : LOCAL_API_BASE_URL;

const feedbackQuestions = [
  { key: 'duration', label: 'Training Time Duration', hindi: 'प्रशिक्षण कालावधि' },
  { key: 'content', label: 'Training Course Content', hindi: 'प्रशिक्षण सामग्री' },
  { key: 'knowledge', label: "Trainer's Knowledge", hindi: 'प्रशिक्षक का ज्ञान' },
  { key: 'presentation', label: 'Presentation Skills', hindi: 'प्रशिक्षण प्रदर्शन' },
  { key: 'increase', label: 'Increase my Knowledge', hindi: 'मेरा ज्ञान बढ़ा' }
];

const ratingOptions = [
  { value: 1, label: 'Not Ok', color: '#e74c3c', icon: <FaRegFrown /> },
  { value: 2, label: 'Improve', color: '#e67e22', icon: <FaRegMeh /> },
  { value: 3, label: 'Ok', color: '#f1c40f', icon: <FaRegSmile /> },
  { value: 4, label: 'Good', color: '#7ed957', icon: <FaSmile /> },
  { value: 5, label: 'Excellent', color: '#27ae60', icon: <FaGrinStars /> }
];

const initialRatings = feedbackQuestions.reduce((acc, q) => {
  acc[q.key] = 0;
  return acc;
}, {});

const FeedbackForm = () => {
  const [header, setHeader] = useState({
    name: '',
    company: '',
    trainingTitle: '',
    date: '',
    place: '',
    trainer: ''
  });
  const [ratings, setRatings] = useState(initialRatings);
  const [overall, setOverall] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleRating = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handleOverall = (value) => {
    setOverall(value);
    if (errors.overall) setErrors((prev) => ({ ...prev, overall: '' }));
  };

  const handleComment = (e) => {
    setComment(e.target.value.slice(0, 255));
  };

  const validate = () => {
    const newErrors = {};
    if (!header.name.trim()) newErrors.name = 'Required';
    if (!header.company.trim()) newErrors.company = 'Required';
    if (!header.trainingTitle.trim()) newErrors.trainingTitle = 'Required';
    if (!header.date.trim()) newErrors.date = 'Required';
    if (!header.place.trim()) newErrors.place = 'Required';
    if (!header.trainer.trim()) newErrors.trainer = 'Required';
    feedbackQuestions.forEach(q => {
      if (!ratings[q.key]) newErrors[q.key] = 'Please rate this item';
    });
    if (!overall) newErrors.overall = 'Overall rating required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      // Scroll to top if errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: header.name,
          company: header.company,
          trainingTitle: header.trainingTitle,
          date: header.date,
          place: header.place,
          trainer: header.trainer,
          ratings: ratings,
          overall: overall,
          comment: comment
        })
      });
      if (!res.ok) throw new Error('Failed to submit feedback');
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setErrors(prev => ({ ...prev, submit: err.message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="feedback-success-container">
        <div className="success-content">
          <div className="success-icon-wrapper">
            <FaCheckCircle className="success-icon" />
          </div>
          <h2>Thank You!</h2>
          <p>Your feedback helps us improve our training standards.</p>
          <button className="btn-return" onClick={() => window.location.reload()}>Submit Another</button>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-page-wrapper">
      <div className="feedback-glass-container">
        <header className="form-header">
          <h1>Training Feedback</h1>
          <p>Please share your honest experience with us</p>
        </header>

        <form className="feedback-form-grid" onSubmit={handleSubmit}>

          {/* Section 1: User Details */}
          <section className="form-section details-grid">
            <div className="input-group">
              <span className="input-icon"><FaUser /></span>
              <input
                name="name"
                placeholder="Full Name"
                value={header.name}
                onChange={handleHeaderChange}
                className={errors.name ? 'error-ring' : ''}
              />
            </div>

            <div className="input-group">
              <span className="input-icon"><FaBuilding /></span>
              <input
                name="company"
                placeholder="Company Name"
                value={header.company}
                onChange={handleHeaderChange}
                className={errors.company ? 'error-ring' : ''}
              />
            </div>

            <div className="input-group">
              <span className="input-icon"><FaBook /></span>
              <input
                name="trainingTitle"
                placeholder="Training Topic"
                value={header.trainingTitle}
                onChange={handleHeaderChange}
                className={errors.trainingTitle ? 'error-ring' : ''}
              />
            </div>

            <div className="input-group">
              <span className="input-icon"><FaChalkboardTeacher /></span>
              <input
                name="trainer"
                placeholder="Trainer Name"
                value={header.trainer}
                onChange={handleHeaderChange}
                className={errors.trainer ? 'error-ring' : ''}
              />
            </div>

            <div className="input-group">
              <span className="input-icon"><FaCalendarAlt /></span>
              <input
                name="date"
                type="date"
                value={header.date}
                onChange={handleHeaderChange}
                className={errors.date ? 'error-ring' : ''}
              />
            </div>

            <div className="input-group">
              <span className="input-icon"><FaMapMarkerAlt /></span>
              <input
                name="place"
                placeholder="Training Location"
                value={header.place}
                onChange={handleHeaderChange}
                className={errors.place ? 'error-ring' : ''}
              />
            </div>
          </section>

          {/* Section 2: Rating Grid */}
          <section className="form-section ratings-section">
            <h3>Course Evaluation</h3>
            <div className="ratings-grid-wrapper">
              {feedbackQuestions.map((q) => (
                <div key={q.key} className="rating-row">
                  <div className="rating-label-col">
                    <span className="question-text">{q.label}</span>
                    <span className="hindi-text">{q.hindi}</span>
                  </div>
                  <div className="rating-options-col">
                    {ratingOptions.map((opt) => (
                      <div
                        key={opt.value}
                        className={`rating-option ${ratings[q.key] === opt.value ? 'selected' : ''}`}
                        onClick={() => handleRating(q.key, opt.value)}
                        style={{ '--option-color': opt.color }}
                      >
                        <span className="opt-icon">{opt.icon}</span>
                        <span className="opt-label">{opt.label}</span>
                      </div>
                    ))}
                  </div>
                  {errors[q.key] && <div className="row-error">!</div>}
                </div>
              ))}
            </div>
          </section>

          {/* Section 3: Overall & Comment */}
          <section className="form-section final-section">
            <div className="overall-rating-container">
              <h3>Overall Experience <span className="hindi">(कुल मिलाकर)</span></h3>
              <div className="rating-options-col large">
                {ratingOptions.map((opt) => (
                  <div
                    key={opt.value}
                    className={`rating-option large ${overall === opt.value ? 'selected' : ''}`}
                    onClick={() => handleOverall(opt.value)}
                    style={{ '--option-color': opt.color }}
                  >
                    <span className="opt-icon">{opt.icon}</span>
                    <span className="opt-label">{opt.label}</span>
                  </div>
                ))}
              </div>
              {errors.overall && <span className="error-text-center">{errors.overall}</span>}
            </div>

            <div className="comment-container">
              <label>Additional Comments ({255 - comment.length} chars left)</label>
              <textarea
                value={comment}
                onChange={handleComment}
                placeholder="Share your thoughts about the training..."
                rows={4}
              />
            </div>
          </section>

          {errors.submit && <div className="submit-error">{errors.submit}</div>}

          <button type="submit" className="glass-submit-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Sending Feedback...' : 'Submit Feedback'}
          </button>

        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;
